/**
 * PricingPage — credit top-up + recurring subscription surface for v2.
 *
 * Renders two stacked sections:
 *
 *   1. **Plans** (recurring subscriptions). Three tier cards backed by
 *      `stripe_service.PLAN_REGISTRY`. Subscribed users see their current
 *      tier highlighted and a "Manage subscription" CTA that opens the
 *      Stripe Customer Portal in a new tab. Hidden when the deploy
 *      doesn't have subscriptions wired up (503 from /subscriptions/me).
 *
 *   2. **Top up** (one-shot packs). Same flow that's shipped today.
 *      Unchanged behavior.
 *
 * Flow (both sections):
 *   1. User picks a plan/pack → POST .../create-session → hard redirect
 *      to Stripe-hosted Checkout (or Portal for "Manage").
 *   2. On return Stripe appends `?topup=success|cancel` (packs) or
 *      `?subscribe=success|cancel` (plans). We poll `/sparks` until the
 *      webhook credits land, then strip the query string so refresh
 *      doesn't re-trigger.
 *
 * All API calls go through `@ponpon/api-client`, whose `request()`
 * wrapper sets `credentials: 'include'` — required for LB session
 * affinity (CLAUDE.md §Production Request Routing).
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '@ponpon/api-client'
import { useSparks } from '../hooks/useSparks'
import { useSubscription, type SubscriptionData } from '../hooks/useSubscription'
import { useAuth } from '../contexts/AuthContext'

interface SubscriptionPlan {
  tier: string
  label: string
  priceUsd: number
  monthlyCredits: number
  perDollar: number
  highlight?: string
}

interface CreditPack {
  id: string
  sparks: number
  priceUsd: number
  label: string
  badge?: string
}

export const SUBSCRIPTION_PLANS: SubscriptionPlan[] = [
  {
    tier: 'hobby',
    label: 'Hobby',
    priceUsd: 10,
    monthlyCredits: 1_000,
    perDollar: 100,
  },
  {
    tier: 'pro',
    label: 'Pro',
    priceUsd: 50,
    monthlyCredits: 8_000,
    perDollar: 160,
    highlight: '1.6× value',
  },
  {
    tier: 'studio',
    label: 'Studio',
    priceUsd: 200,
    monthlyCredits: 40_000,
    perDollar: 200,
    highlight: '2× value',
  },
]

export const CREDIT_PACKS: CreditPack[] = [
  { id: 'credits_500', sparks: 500, priceUsd: 5, label: 'Starter' },
  { id: 'credits_2000', sparks: 2000, priceUsd: 20, label: 'Standard' },
  { id: 'credits_5000', sparks: 5000, priceUsd: 50, label: 'Pro' },
  { id: 'credits_11000', sparks: 11000, priceUsd: 100, label: 'Studio', badge: '+10%' },
]

export function PricingPage() {
  const { session } = useAuth()
  const { data: sparks, isLoading: sparksLoading, refetch: refetchSparks } = useSparks()
  const {
    data: subscription,
    isLoading: subLoading,
    refetch: refetchSubscription,
  } = useSubscription()
  const [pendingPackId, setPendingPackId] = useState<string | null>(null)
  const [pendingPlanTier, setPendingPlanTier] = useState<string | null>(null)
  const [portalPending, setPortalPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [topupStatus, setTopupStatus] = useState<'idle' | 'processing' | 'cancelled'>('idle')
  const [subscribeStatus, setSubscribeStatus] = useState<
    'idle' | 'processing' | 'cancelled'
  >('idle')

  // On return from Stripe Checkout (packs or plans): poll /sparks +
  // /subscriptions/me for a few seconds until the webhook lands, then
  // strip the query string so a refresh doesn't loop.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const topup = params.get('topup')
    const subscribe = params.get('subscribe')

    const isTopupReturn = topup === 'success' || topup === 'cancel'
    const isSubscribeReturn = subscribe === 'success' || subscribe === 'cancel'
    if (!isTopupReturn && !isSubscribeReturn) return

    if (topup === 'success') setTopupStatus('processing')
    else if (topup === 'cancel') setTopupStatus('cancelled')
    if (subscribe === 'success') setSubscribeStatus('processing')
    else if (subscribe === 'cancel') setSubscribeStatus('cancelled')

    const clearQuery = () => {
      params.delete('topup')
      params.delete('subscribe')
      params.delete('session')
      const newSearch = params.toString()
      window.history.replaceState(
        null,
        '',
        window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash,
      )
    }

    if (topup === 'cancel' || subscribe === 'cancel') {
      clearQuery()
      return
    }

    let cancelled = false
    let ticks = 0
    const interval = window.setInterval(() => {
      if (cancelled) return
      void refetchSparks()
      if (subscribe === 'success') void refetchSubscription()
      ticks += 1
      if (ticks >= 8) {
        window.clearInterval(interval)
        setTopupStatus('idle')
        setSubscribeStatus('idle')
        clearQuery()
      }
    }, 1500)
    return () => {
      cancelled = true
      window.clearInterval(interval)
    }
  }, [refetchSparks, refetchSubscription])

  const handlePackClick = async (packId: string) => {
    setError(null)
    setPendingPackId(packId)
    try {
      const result = await api.createCheckoutSession(packId)
      window.location.href = result.session_url
    } catch (err: unknown) {
      setPendingPackId(null)
      setError(parseApiError(err))
    }
  }

  const handlePlanClick = async (planTier: string) => {
    setError(null)
    setPendingPlanTier(planTier)
    try {
      const result = await api.createSubscriptionCheckoutSession(planTier)
      window.location.href = result.session_url
    } catch (err: unknown) {
      setPendingPlanTier(null)
      setError(parseApiError(err))
    }
  }

  const handleManageClick = async () => {
    setError(null)
    setPortalPending(true)
    try {
      const result = await api.createPortalSession()
      // Open the Portal in the same tab so the Stripe-Hosted "return to
      // app" link comes back here.
      window.location.href = result.portal_url
    } catch (err: unknown) {
      setPortalPending(false)
      setError(parseApiError(err))
    }
  }

  // Logged-out visitors see the real prices.
  //
  // This used to be a stub reading "Sign in to buy credits and run
  // generations" — no tiers, no packs, no numbers. Pricing is an evaluation
  // surface: someone arriving from an ad or a shared link is deciding whether
  // to sign up AT ALL, and a pricing page with no pricing gives them nothing
  // to decide on. Gating it also costs the search traffic that pricing pages
  // reliably attract.
  //
  // Nothing here needs auth — SUBSCRIPTION_PLANS and CREDIT_PACKS are static
  // constants. Only checkout does, so the cards are rendered read-only and
  // every CTA routes to /login. The authenticated path below is untouched.
  if (!session) {
    return (
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
        <header className="mb-8">
          <h1 className="text-text text-[2rem] mb-2" style={{ fontWeight: 600 }}>
            Pricing
          </h1>
          <p className="text-text-2">
            Every generation is billed in credits. Start free — new accounts get 1,000.
          </p>
        </header>

        <section className="mb-10">
          <h2 className="text-text text-[1.1rem] mb-3" style={{ fontWeight: 600 }}>
            Monthly plans
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {SUBSCRIPTION_PLANS.map((plan) => (
              <Link
                key={plan.tier}
                to="/login"
                data-testid={`anon-plan-${plan.tier}`}
                className="glass rounded-md p-4 block transition-colors hover:bg-bg-2/80 hover:border-accent/40"
                style={{ borderRadius: 'var(--r-md)' }}
              >
                <div className="flex items-baseline justify-between mb-1">
                  <span className="text-text" style={{ fontWeight: 600 }}>
                    {plan.label}
                  </span>
                  {plan.highlight && (
                    <span className="t-mono text-[0.7rem] text-accent">{plan.highlight}</span>
                  )}
                </div>
                <div className="text-text text-[1.5rem]" style={{ fontWeight: 600 }}>
                  ${plan.priceUsd}
                  <span className="text-text-3 text-[0.85rem]"> /mo</span>
                </div>
                <div className="text-text-2 text-[0.86rem] mt-1">
                  {plan.monthlyCredits.toLocaleString()} credits a month
                </div>
                <div className="t-mono text-[0.72rem] text-text-3 mt-2">
                  {plan.perDollar} credits per $1
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="text-text text-[1.1rem] mb-3" style={{ fontWeight: 600 }}>
            Or top up as you go
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {CREDIT_PACKS.map((pack) => (
              <Link
                key={pack.id}
                to="/login"
                data-testid={`anon-pack-${pack.id}`}
                className="glass rounded-md p-4 block transition-colors hover:bg-bg-2/80 hover:border-accent/40"
                style={{ borderRadius: 'var(--r-md)' }}
              >
                <div className="flex items-baseline justify-between mb-1">
                  <span className="text-text-2 text-[0.86rem]">{pack.label}</span>
                  {pack.badge && (
                    <span className="t-mono text-[0.7rem] text-accent">{pack.badge}</span>
                  )}
                </div>
                <div className="text-text text-[1.25rem]" style={{ fontWeight: 600 }}>
                  ${pack.priceUsd}
                </div>
                <div className="text-text-2 text-[0.86rem]">
                  {pack.sparks.toLocaleString()} credits
                </div>
              </Link>
            ))}
          </div>
        </section>

        <div className="flex items-center gap-3">
          <Link to="/login" className="btn-accent inline-flex">
            Get started free
          </Link>
          <span className="text-text-3 text-[0.86rem]">No card needed to try it.</span>
        </div>
      </div>
    )
  }

  // When subscriptions endpoint returns null AND we're not still loading,
  // hide the plans section entirely (deploy hasn't wired up the feature).
  const showPlansSection = subLoading || subscription !== null || subscriptionsAvailable()

  return (
    <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
      <header className="mb-8">
        <h1
          className="text-text text-[2rem] sm:text-[2.4rem] mb-2"
          style={{
            fontWeight: 600,
            fontVariationSettings: '"wdth" 96, "opsz" 96',
            letterSpacing: '-0.025em',
          }}
        >
          Credits
        </h1>
        <p className="text-text-2 text-[0.96rem] leading-relaxed">
          Pay-as-you-go. 100 credits = $1. Subscribe for monthly credit grants at
          better rates, or top up as needed.
        </p>
      </header>

      {/* Current balance + subscription chip */}
      <div
        className="glass rounded-md p-4 mb-8 flex items-center gap-3"
        data-testid="current-balance"
      >
        <svg
          className="w-5 h-5 text-accent shrink-0"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden
        >
          <path d="M13 3L4 14h7l-2 7 9-11h-7l2-7z" />
        </svg>
        <div className="flex-1">
          <div className="text-text-3 t-mono-caps text-[0.6rem]">Available</div>
          <div
            className="text-text text-[1.4rem] tabular-nums leading-none"
            style={{ fontWeight: 600 }}
          >
            {sparksLoading ? '…' : (sparks?.balance ?? 0).toLocaleString()}{' '}
            <span className="text-text-3 text-[0.86rem]" style={{ fontWeight: 400 }}>
              credits
            </span>
          </div>
        </div>
        {subscription && subscription.status === 'active' && (
          <span className="pill pill--accent" data-testid="active-subscription-chip">
            <span>
              {planLabel(subscription.plan_tier)} · renews{' '}
              {formatRenewalDate(subscription.current_period_end)}
            </span>
          </span>
        )}
        {topupStatus === 'processing' && (
          <span className="pill pill--accent">
            <span className="live-dot" />
            <span>processing top-up</span>
          </span>
        )}
        {subscribeStatus === 'processing' && (
          <span className="pill pill--accent" data-testid="processing-subscription-chip">
            <span className="live-dot" />
            <span>processing subscription</span>
          </span>
        )}
        {topupStatus === 'cancelled' && (
          <span className="pill">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-text-3" />
            <span>top-up cancelled</span>
          </span>
        )}
        {subscribeStatus === 'cancelled' && (
          <span className="pill">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-text-3" />
            <span>subscription cancelled</span>
          </span>
        )}
      </div>

      {/* Subscription plans (hidden when feature off on this deploy). */}
      {showPlansSection && (
        <section data-testid="subscription-plans" className="mb-10">
          <div className="flex items-baseline justify-between mb-3">
            <h2
              className="text-text-2 t-mono-caps text-[0.7rem]"
              style={{ letterSpacing: '0.16em' }}
            >
              Plans
            </h2>
            {subscription && subscription.status === 'active' && (
              <button
                type="button"
                onClick={handleManageClick}
                disabled={portalPending}
                data-testid="manage-subscription-btn"
                className="text-text-2 text-[0.86rem] hover:text-text underline-offset-2 hover:underline disabled:opacity-50"
              >
                {portalPending ? 'Opening portal…' : 'Manage subscription'}
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {SUBSCRIPTION_PLANS.map((plan) => {
              const isCurrent =
                subscription &&
                subscription.status === 'active' &&
                subscription.plan_tier === plan.tier
              const isPending = pendingPlanTier === plan.tier
              const isAnyPlanPending = pendingPlanTier !== null
              const ctaLabel = (() => {
                if (isCurrent) return 'Current plan'
                if (isPending) return 'Redirecting…'
                if (subscription && subscription.status === 'active') {
                  return `Switch to ${plan.label}`
                }
                return 'Subscribe'
              })()
              return (
                <button
                  key={plan.tier}
                  type="button"
                  onClick={() => (isCurrent ? handleManageClick() : handlePlanClick(plan.tier))}
                  disabled={isAnyPlanPending || (isCurrent && portalPending)}
                  data-testid={`subscription-plan-${plan.tier}`}
                  data-plan-tier={plan.tier}
                  className={`glass rounded-md p-4 text-left transition-colors hover:bg-bg-2/80 disabled:opacity-50 disabled:cursor-not-allowed ${
                    isCurrent ? 'border-accent/60' : 'hover:border-accent/40'
                  }`}
                  style={{ borderRadius: 'var(--r-md)' }}
                >
                  <div className="flex items-baseline justify-between mb-1">
                    <span className="t-mono-caps text-text-3 text-[0.62rem]">
                      {plan.label}
                    </span>
                    {plan.highlight && (
                      <span className="t-mono text-accent text-[0.66rem]">
                        {plan.highlight}
                      </span>
                    )}
                  </div>
                  <div
                    className="text-text text-[1.4rem] tabular-nums leading-none mt-1"
                    style={{ fontWeight: 600 }}
                  >
                    {plan.monthlyCredits.toLocaleString()}
                  </div>
                  <div className="text-text-3 text-[0.78rem] mt-0.5">credits / month</div>
                  <div className="border-t border-line mt-3 pt-3 flex items-baseline justify-between">
                    <span
                      className="text-text text-[1.05rem] tabular-nums"
                      style={{ fontWeight: 600 }}
                    >
                      ${plan.priceUsd}
                      <span className="text-text-3 text-[0.72rem] ml-0.5">/mo</span>
                    </span>
                    <span className="text-text-3 text-[0.74rem]">{ctaLabel}</span>
                  </div>
                </button>
              )
            })}
          </div>
          <p className="text-text-3 text-[0.78rem] mt-2">
            Credit grants land on each renewal. Cancel anytime from the Customer Portal.
          </p>
        </section>
      )}

      {/* Pack grid (top-up). */}
      <h2
        className="text-text-2 t-mono-caps text-[0.7rem] mb-3"
        style={{ letterSpacing: '0.16em' }}
      >
        {showPlansSection ? 'Or top up without a subscription' : 'Top up'}
      </h2>
      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3"
        data-testid="credit-packs"
      >
        {CREDIT_PACKS.map((pack) => {
          const isPending = pendingPackId === pack.id
          const isAnyPending = pendingPackId !== null
          return (
            <button
              key={pack.id}
              onClick={() => handlePackClick(pack.id)}
              disabled={isAnyPending}
              data-testid={`credit-pack-${pack.id}`}
              data-pack-sparks={pack.sparks}
              className="glass rounded-md p-4 text-left transition-colors hover:bg-bg-2/80 hover:border-accent/40 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ borderRadius: 'var(--r-md)' }}
            >
              <div className="flex items-baseline justify-between mb-1">
                <span className="t-mono-caps text-text-3 text-[0.62rem]">{pack.label}</span>
                {pack.badge && (
                  <span className="t-mono text-accent text-[0.66rem]">{pack.badge}</span>
                )}
              </div>
              <div
                className="text-text text-[1.4rem] tabular-nums leading-none mt-1"
                style={{ fontWeight: 600 }}
              >
                {pack.sparks.toLocaleString()}
              </div>
              <div className="text-text-3 text-[0.78rem] mt-0.5">credits</div>
              <div className="border-t border-line mt-3 pt-3 flex items-baseline justify-between">
                <span
                  className="text-text text-[1.05rem] tabular-nums"
                  style={{ fontWeight: 600 }}
                >
                  ${pack.priceUsd}
                </span>
                <span className="text-text-3 text-[0.74rem]">
                  {isPending ? 'Redirecting…' : 'Buy'}
                </span>
              </div>
            </button>
          )
        })}
      </div>

      {error && (
        <div
          className="mt-6 rounded-md border border-err/30 bg-err/10 px-3 py-2 text-err text-[0.86rem]"
          data-testid="pricing-error"
        >
          {error}
        </div>
      )}
    </div>
  )
}


function planLabel(tier: string): string {
  switch (tier) {
    case 'hobby':
      return 'Hobby'
    case 'pro':
      return 'Pro'
    case 'studio':
      return 'Studio'
    default:
      return tier
  }
}

function formatRenewalDate(iso: string | null): string {
  if (!iso) return 'soon'
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return 'soon'
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  } catch {
    return 'soon'
  }
}

function subscriptionsAvailable(): boolean {
  // Vite build-time flag — false hides the plans section entirely.
  // When unset, default to TRUE so the section renders and a 503 from
  // /subscriptions/me is what hides it (server is the source of truth).
  const raw = (import.meta as ImportMeta & { env?: Record<string, string> }).env?.[
    'VITE_SUBSCRIPTIONS_ENABLED'
  ]
  if (raw === undefined) return true
  return raw.toLowerCase() !== 'false' && raw !== '0'
}

/**
 * Surface the backend's structured error code in human-readable form
 * so users (and we) can tell "Stripe not set up on this deploy" from
 * "your auth token is bad" without opening devtools.
 */
function parseApiError(err: unknown): string {
  if (!(err instanceof Error)) return 'Checkout failed. Please try again.'
  const message = err.message || ''
  if (message.includes('STRIPE_NOT_CONFIGURED')) {
    return (
      'Credit top-up isn’t enabled on this deploy yet. ' +
      'Local dev: use the daily claim endpoint instead.'
    )
  }
  if (message.includes('SUBSCRIPTIONS_NOT_CONFIGURED')) {
    return (
      'Subscriptions aren’t enabled on this deploy yet. ' +
      'You can still top up credits below.'
    )
  }
  if (message.includes('STRIPE_NOT_AVAILABLE_FOR_DEBUG_USER')) {
    return 'Local dev user can’t purchase credits — use the daily claim or debug-balance helper.'
  }
  if (message.includes('UNKNOWN_PACK')) {
    return 'That credit pack is unavailable. Reload the page and try again.'
  }
  if (message.includes('UNKNOWN_PLAN')) {
    return 'That plan is unavailable. Reload the page and try again.'
  }
  if (message.includes('NO_STRIPE_CUSTOMER')) {
    return 'Subscribe or buy a pack first — the Customer Portal opens after your first checkout.'
  }
  if (message.includes('MISSING_ORIGIN')) {
    return 'Browser didn’t send an Origin header. Try a hard refresh.'
  }
  return message || 'Checkout failed. Please try again.'
}
